"use client"

import { useRef, useEffect, useState } from "react"

interface HeroDisplayProps {
  selectedHeroType: string
  position?: {
    top?: string
    left?: string
    right?: string
    zIndex?: number
  }
  isDesktop?: boolean
  isWalkingOut?: boolean
}

// Hero sprite configurations for walk animations
interface HeroSpriteConfig {
  sheet: string
  frameWidth: number
  frameHeight: number
  frameCount: number
  offsetX: number
}

const heroWalkConfigs: Record<string, HeroSpriteConfig> = {
  knight: {
    sheet: "/images/heroes/Paladin_Walk.png",
    frameWidth: 128,
    frameHeight: 128,
    frameCount: 8,
    offsetX: -16, // Character is positioned left of center in eastward sprite
  },
  ranger: {
    sheet: "/images/heroes/Ranger_Walk.png",
    frameWidth: 128,
    frameHeight: 128,
    frameCount: 12,
    offsetX: -16,
  },
  siegesmith: {
    sheet: "/images/heroes/Siegesmith_Walk.png",
    frameWidth: 128,
    frameHeight: 128,
    frameCount: 10,
    offsetX: -16,
  },
}

// Hero idle sprite configurations
const heroIdleConfigs: Record<string, HeroSpriteConfig> = {
  knight: {
    sheet: "/images/heroes/Paladin_Idle_2.png",
    frameWidth: 128,
    frameHeight: 128,
    frameCount: 5, // 640px / 128px = 5 frames
    offsetX: -16,
  },
  ranger: {
    sheet: "/images/heroes/Ranger_Idle.png",
    frameWidth: 128,
    frameHeight: 128,
    frameCount: 6, // 768px / 128px = 6 frames
    offsetX: -16,
  },
  siegesmith: {
    sheet: "/images/heroes/Siegesmith_Idle.png",
    frameWidth: 128,
    frameHeight: 128,
    frameCount: 6,
    offsetX: -16,
  },
}

// Walk-in animation duration in milliseconds (must match CSS animation duration)
const WALK_IN_DURATION = 3000

const heroDisplayNames: Record<string, string> = {
  knight: "Sir Aldric",
  ranger: "Sylara",
  siegesmith: "Dainn Emberash",
}

export const HeroDisplay = ({
  selectedHeroType,
  position = {},
  isDesktop = false,
  isWalkingOut = false,
}: HeroDisplayProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [walkSpriteImage, setWalkSpriteImage] = useState<HTMLImageElement | null>(null)
  const [idleSpriteImage, setIdleSpriteImage] = useState<HTMLImageElement | null>(null)
  const animationRef = useRef<number | null>(null)
  const frameRef = useRef(0)
  const lastFrameTimeRef = useRef(0)
  const mountTimeRef = useRef(Date.now())
  const hasReachedCenterRef = useRef(false)

  const heroName = heroDisplayNames[selectedHeroType] || heroDisplayNames.knight
  const walkConfig = heroWalkConfigs[selectedHeroType] || heroWalkConfigs.knight
  const idleConfig = heroIdleConfigs[selectedHeroType] || heroIdleConfigs.knight

  // Animation speed in milliseconds per frame
  const WALK_FRAME_DURATION = 100 // 10 fps for walk animation
  const IDLE_FRAME_DURATION = 150 // Slightly slower for idle

  // Canvas size - 50% bigger than original 96px
  const CANVAS_SIZE = 144

  // Load walk sprite sheet
  useEffect(() => {
    const img = new Image()
    img.crossOrigin = "anonymous"
    img.onload = () => setWalkSpriteImage(img)
    img.src = walkConfig.sheet
  }, [walkConfig.sheet])

  // Load idle sprite sheet
  useEffect(() => {
    const img = new Image()
    img.crossOrigin = "anonymous"
    img.onload = () => setIdleSpriteImage(img)
    img.src = idleConfig.sheet
  }, [idleConfig.sheet])

  // Track when walk-in animation completes
  useEffect(() => {
    // Reset state when component mounts
    mountTimeRef.current = Date.now()
    hasReachedCenterRef.current = false
    frameRef.current = 0

    // If not walking out, set timer for when walk-in completes
    if (!isWalkingOut) {
      const timer = setTimeout(() => {
        hasReachedCenterRef.current = true
        frameRef.current = 0 // Reset frame for idle animation
      }, WALK_IN_DURATION)

      return () => clearTimeout(timer)
    }
  }, [isWalkingOut])

  // Reset to walking when walking out starts
  useEffect(() => {
    if (isWalkingOut) {
      hasReachedCenterRef.current = false
      frameRef.current = 0
    }
  }, [isWalkingOut])

  // Animation loop - uses refs to avoid restarting on state changes
  useEffect(() => {
    if (!canvasRef.current) return

    // Wait for at least the walk sprite to be available
    if (!walkSpriteImage) return

    const canvas = canvasRef.current
    const ctx = canvas.getContext("2d")
    if (!ctx) return

    const animate = (timestamp: number) => {
      // Determine which sprite and config to use inside the loop (using ref for hasReachedCenter)
      const isIdle = hasReachedCenterRef.current && !isWalkingOut
      // If idle sprite not loaded yet, keep using walk sprite to avoid flicker
      const useIdleSprite = isIdle && idleSpriteImage
      const currentConfig = useIdleSprite ? idleConfig : walkConfig
      const currentSprite = useIdleSprite ? idleSpriteImage : walkSpriteImage
      const frameDuration = useIdleSprite ? IDLE_FRAME_DURATION : WALK_FRAME_DURATION

      // Update frame based on time
      if (timestamp - lastFrameTimeRef.current >= frameDuration) {
        frameRef.current = (frameRef.current + 1) % currentConfig.frameCount
        lastFrameTimeRef.current = timestamp
      }

      // Clear canvas
      ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE)

      // Save context state
      ctx.save()

      // Hero faces left when walking out (mirrored), right when walking in or idle
      // Sprites face east by default, so we need to mirror for walking left
      // The character within the sprite frame is not centered - it's offset to one side
      // to leave room for movement animation. We apply offsetX to center the visible character.

      // Calculate horizontal offset to center the character
      // When facing east (not mirrored): character is left of center, shift right
      // When facing west (mirrored): character appears right of center after mirror, shift left
      const scale = CANVAS_SIZE / currentConfig.frameWidth
      const scaledOffsetX = currentConfig.offsetX * scale

      if (isWalkingOut) {
        // Walking out to the left - sprite should face left (mirror)
        ctx.translate(CANVAS_SIZE, 0)
        ctx.scale(-1, 1)
        // After mirroring, the offset direction is reversed
      }
      // Walking in from the left or idle - sprite faces right (no mirror needed)

      // Calculate source position on sprite sheet
      const sourceX = frameRef.current * currentConfig.frameWidth
      const sourceY = 0

      // Draw sprite with horizontal offset to center the character visually
      ctx.imageSmoothingEnabled = false

      // Apply offset: negative offsetX means character is left of center in east-facing sprite
      // So we draw further right to center it. When mirrored, the offset effect is naturally reversed.
      const drawX = isWalkingOut ? -scaledOffsetX : scaledOffsetX

      if (currentSprite) {
        ctx.drawImage(
          currentSprite,
          sourceX,
          sourceY,
          currentConfig.frameWidth,
          currentConfig.frameHeight,
          drawX,
          0,
          CANVAS_SIZE,
          CANVAS_SIZE
        )
      }

      // Restore context state
      ctx.restore()

      animationRef.current = requestAnimationFrame(animate)
    }

    animationRef.current = requestAnimationFrame(animate)

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [walkSpriteImage, idleSpriteImage, walkConfig, idleConfig, isWalkingOut, IDLE_FRAME_DURATION, WALK_FRAME_DURATION])

  // Determine animation class based on state
  const getAnimationClass = () => {
    if (isWalkingOut) {
      return isDesktop ? "animate-hero-walk-out-desktop" : "animate-hero-walk-out-mobile"
    }
    return isDesktop ? "animate-hero-walk-in-desktop" : "animate-hero-walk-in-mobile"
  }

  return (
    <div
      id="hero-display"
      className={`absolute ${getAnimationClass()}`}
      style={{
        top: position.top,
        right: position.right,
        zIndex: position.zIndex || 5,
        filter:
          "drop-shadow(0 0 80px rgba(255, 140, 0, 1)) drop-shadow(0 0 50px rgba(255, 140, 0, 0.9)) drop-shadow(0 0 30px rgba(255, 140, 0, 0.8))",
      }}
    >
      <canvas
        ref={canvasRef}
        width={CANVAS_SIZE}
        height={CANVAS_SIZE}
        aria-label={heroName}
        style={{
          imageRendering: "pixelated",
          width: CANVAS_SIZE,
          height: CANVAS_SIZE,
        }}
      />
    </div>
  )
}
